import React, { FC, useContext, useState } from 'react';

import { FetchResult } from '@apollo/client';
import { Button, H3 } from '@smartshell/ui-kit';

import { SettingsLock } from 'components';
import { useIsFeatureAvailable } from 'features';
import { LocalizationContext } from 'localization';
import {
  ESettingsAliases,
  ISetSettingResponse,
  SettingsContext,
} from 'settings';
import { IAutorunApplication } from 'types';
import { shallowEqual } from 'utils';

import s from './Autorun.module.scss';
import { AutorunTable } from './AutorunTable';
import { ManageAutorunApplicationMW } from './ManageAutorunApplicationMW';

export const Autorun: FC = () => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { autorun },
    },
    actions: { setSetting },
  } = useContext(SettingsContext);
  const isAvailable = useIsFeatureAvailable('autostart');

  const [isAutorunManagerVisible, setIsAutorunManagerVisible] = useState(false);
  const [selectedApp, setSelectedApp] = useState<IAutorunApplication | null>(
    null
  );

  const deleteApplication = (
    application: IAutorunApplication
  ): Promise<FetchResult<ISetSettingResponse>> =>
    setSetting({
      alias: ESettingsAliases.SHELL_AUTORUN,
      value: JSON.stringify([
        ...autorun.filter((app) => !shallowEqual(app, application)),
      ]),
    });

  const editApplication = (application: IAutorunApplication): void => {
    setSelectedApp(application);
    setIsAutorunManagerVisible(true);
  };

  return (
    <div className={s.root}>
      {isAutorunManagerVisible && (
        <ManageAutorunApplicationMW
          application={selectedApp}
          createApplication={(application) =>
            setSetting({
              alias: ESettingsAliases.SHELL_AUTORUN,
              value: JSON.stringify([...autorun, application]),
            })
          }
          updateApplication={(application) =>
            setSetting({
              alias: ESettingsAliases.SHELL_AUTORUN,
              value: JSON.stringify([
                ...autorun.filter((app) => !shallowEqual(app, selectedApp)),
                application,
              ]),
            })
          }
          deleteApplication={deleteApplication}
          onClose={() => {
            setIsAutorunManagerVisible(false);
            setSelectedApp(null);
          }}
        />
      )}
      <div className={s.header}>
        <div className={s.left_block}>
          <div className={s.title_block}>
            <H3 className={s.title}>{t('setting.shell.autorun')}</H3>
            {!isAvailable && <SettingsLock />}
          </div>
          <span className={s.subtitle}>
            {t('settings.shell.autorun.description')}
          </span>
        </div>

        <Button
          className={s['add-application']}
          size="s"
          variant="secondary"
          onClick={() => {
            setSelectedApp(null);
            setIsAutorunManagerVisible(true);
          }}
          iconName={'CirclePlusIcon'}
          disabled={!isAvailable}
        >
          {t('add')}
        </Button>
      </div>
      {autorun.length > 0 && (
        <AutorunTable
          autorun={autorun}
          editApplication={editApplication}
          deleteApplication={deleteApplication}
        />
      )}
    </div>
  );
};
